import jsPDF from 'jspdf';

const COLORS = {
  dark: [15, 23, 42] as [number, number, number],
  gold: [196, 167, 125] as [number, number, number],
  white: [255, 255, 255] as [number, number, number],
  gray: [156, 163, 175] as [number, number, number],
  text: [55, 65, 81] as [number, number, number],
};

interface RechnungData {
  rechnungsnummer: string;
  unternehmen: string;
  bezeichnung?: string;
  datum?: string;
  netto?: number;
  brutto: number;
  steuer?: number;
  steuersatz?: string;
  waehrung?: string;
  status: string;
  zahlungsart?: string;
  iban?: string;
  verwendungszweck?: string;
  faelligkeitsdatum?: string;
  notizen?: string | null;
  bezahlt_timestamp?: string | null;
  bezahlt_von?: string | null;
  freigegeben?: boolean;
  freigegeben_von?: string | null;
  freigegeben_am?: string | null;
  objekt_adresse?: string;
  objektnummer?: string;
  profiles?: { full_name: string } | null;
}

interface TimelineItem {
  typ: string;
  inhalt: string | null;
  created_at: string;
  user?: { full_name: string } | null;
  ziel_user?: { full_name: string } | null;
}

function fmtDate(d?: string | null): string {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtDateTime(d: string): string {
  const dt = new Date(d);
  return `${dt.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${dt.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
}

function fmtEuro(v?: number | null): string {
  if (v == null) return '–';
  return `${Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €`;
}

const TYPE_LABELS: Record<string, string> = {
  kommentar: 'Kommentar',
  zuweisung: 'Zuweisung',
  tech_pruefung_angefordert: 'Sachl. & rechn. Prüfung',
  tech_pruefung_ergebnis: 'Prüfungsergebnis',
  freigabe_angefordert: 'Freigabe angefordert',
  freigabe_ergebnis: 'Freigabe erteilt',
  status_aenderung: 'Status geändert',
  bezahlung: 'Bezahlt',
  et_weitergeleitet: 'An ET weitergeleitet',
};

export function exportSingleRechnungPdf(rechnung: RechnungData, timeline: TimelineItem[]): void {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 18;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  const datum = new Date().toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });

  const checkPage = (needed: number) => {
    if (y + needed > pageHeight - 20) {
      pdf.addPage();
      y = margin;
    }
  };

  // ── Header ──
  pdf.setFillColor(...COLORS.dark);
  pdf.rect(0, 0, pageWidth, 38, 'F');
  pdf.setFillColor(...COLORS.gold);
  pdf.rect(0, 38, pageWidth, 1.5, 'F');

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(16);
  pdf.setTextColor(...COLORS.white);
  pdf.text(rechnung.unternehmen || 'Rechnung', margin, 14);

  pdf.setFontSize(10);
  pdf.setTextColor(...COLORS.gold);
  pdf.text(`#${rechnung.rechnungsnummer}`, margin, 23);

  pdf.setFontSize(7);
  pdf.setTextColor(180, 180, 180);
  pdf.text(`Exportiert am ${datum}`, margin, 34);

  // Betrag + Status rechts
  pdf.setFontSize(14);
  pdf.setTextColor(...COLORS.white);
  pdf.text(fmtEuro(rechnung.brutto), pageWidth - margin, 14, { align: 'right' });
  pdf.setFontSize(8);
  pdf.text(rechnung.status.toUpperCase(), pageWidth - margin, 23, { align: 'right' });

  y = 48;

  // ── Metadaten ──
  const col = contentWidth / 3;

  const drawField = (label: string, value: string, x: number) => {
    pdf.setFontSize(6.5);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...COLORS.gray);
    pdf.text(label.toUpperCase(), x, y);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...COLORS.text);
    // Wert auf Spaltenbreite umbrechen (max. 2 Zeilen, sonst mit „…" kürzen),
    // damit lange Werte (Bezeichnung/Objekt) nicht in die Nachbarspalte laufen.
    const maxW = col - 4;
    let lines = pdf.splitTextToSize(value || '–', maxW) as string[];
    if (lines.length > 2) {
      lines = lines.slice(0, 2);
      let last = lines[1];
      while (last.length > 1 && pdf.getTextWidth(last + '…') > maxW) last = last.slice(0, -1);
      lines[1] = last + '…';
    }
    lines.forEach((ln, i) => pdf.text(ln, x, y + 5 + i * 3.4));
  };

  drawField('Bezeichnung', rechnung.bezeichnung || '–', margin);
  drawField('Objekt', rechnung.objekt_adresse || rechnung.objektnummer || '–', margin + col);
  drawField('Zahlungsart', rechnung.zahlungsart || '–', margin + col * 2);
  y += 14;

  drawField('Netto', fmtEuro(rechnung.netto), margin);
  drawField('MwSt.', rechnung.steuersatz ? `${rechnung.steuersatz}%` : '–', margin + col);
  drawField('Brutto', fmtEuro(rechnung.brutto), margin + col * 2);
  y += 14;

  drawField('Belegdatum', fmtDate(rechnung.datum), margin);
  drawField('Fällig am', fmtDate(rechnung.faelligkeitsdatum), margin + col);
  drawField('Bezahlt am', fmtDate(rechnung.bezahlt_timestamp), margin + col * 2);
  y += 14;

  if (rechnung.bezahlt_von || rechnung.profiles?.full_name) {
    drawField('Bezahlt von', rechnung.profiles?.full_name || '–', margin);
    if (rechnung.freigegeben) {
      drawField('Freigegeben von', rechnung.freigegeben_von || '–', margin + col);
      drawField('Freigegeben am', fmtDate(rechnung.freigegeben_am), margin + col * 2);
    }
    y += 14;
  }

  if (rechnung.iban) {
    drawField('IBAN', rechnung.iban, margin);
    drawField('Verwendungszweck', rechnung.verwendungszweck || '–', margin + col);
    y += 14;
  }

  // ── Divider ──
  pdf.setDrawColor(230, 230, 230);
  pdf.line(margin, y, pageWidth - margin, y);
  y += 6;

  // ── Notizen ──
  if (rechnung.notizen) {
    checkPage(20);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...COLORS.dark);
    pdf.text('NOTIZEN', margin, y);
    y += 5;

    pdf.setFontSize(8.5);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...COLORS.text);
    const lines = pdf.splitTextToSize(rechnung.notizen, contentWidth);
    for (const line of lines) {
      checkPage(5);
      pdf.text(line, margin, y);
      y += 4.5;
    }
    y += 4;

    pdf.setDrawColor(230, 230, 230);
    pdf.line(margin, y, pageWidth - margin, y);
    y += 6;
  }

  // ── Verlauf ──
  if (timeline.length > 0) {
    checkPage(15);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...COLORS.dark);
    pdf.text(`VERLAUF (${timeline.length})`, margin, y);
    y += 7;

    for (const t of timeline) {
      checkPage(12);
      const typeLabel = TYPE_LABELS[t.typ] || t.typ;
      const userName = t.user?.full_name || 'System';

      // Type + Date line
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...COLORS.dark);
      pdf.text(typeLabel, margin, y);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...COLORS.gray);
      pdf.text(`${userName} · ${fmtDateTime(t.created_at)}`, margin + 50, y);
      y += 4;

      // Content
      if (t.inhalt) {
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(...COLORS.text);
        const cLines = pdf.splitTextToSize(t.inhalt, contentWidth - 4);
        for (const line of cLines) {
          checkPage(5);
          pdf.text(line, margin + 2, y);
          y += 4;
        }
      }
      y += 3;
    }
  }

  // ── Footer ──
  const pageCount = pdf.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(6.5);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...COLORS.gray);
    pdf.text('ImmoSchwede · Rechnungsexport', margin, pageHeight - 8);
    pdf.text(`Seite ${i} / ${pageCount}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
  }

  pdf.save(`Rechnung_${rechnung.rechnungsnummer}_${rechnung.unternehmen.substring(0, 20).replace(/[^a-zA-Z0-9äöüÄÖÜß]/g, '_')}.pdf`);
}
